import React, { useEffect } from 'react';
import { usePageSEO } from '../hooks/usePageSEO';
import Hero from '../components/home/Hero';
const TrustedBy = React.lazy(() => import('../components/home/TrustedBy'));
const Features = React.lazy(() => import('../components/home/Features'));
const Calculators = React.lazy(() => import('../components/home/Calculators'));
const CTA = React.lazy(() => import('../components/home/CTA'));
const NewsletterSignup = React.lazy(() => import('../components/common/NewsletterSignup'));

// Preload components helper
const preloadHomeSections = () => {
  import('../components/home/Features');
  import('../components/home/TrustedBy');
  import('../components/home/Calculators');
  import('../components/home/CTA');
  import('../components/common/NewsletterSignup');
};

const Home = () => {
  const { HelmetComponent } = usePageSEO({
    title: 'FinCalc — Free AI Financial & Mortgage Estimator (TikTok, SIP, EMI, Loans)',
    description: 'Free AI-powered financial estimator tools: TikTok money calculator, SIP, SWP, mortgage, home loan EMI, & refinance. Get instant, accurate AI insights.',
    canonical: 'https://financecal.org/',
    keywords: 'TikTok money calculator, TikTok earnings estimator, TikTok money withdrawal, SIP calculator, SWP calculator, GST calculator India, mortgage estimator, car loan payment estimator, natwest loan calculator, rbs mortgage calculator, hsbc loan calculator uae, hdfc home loan calculator, loan calculator with trade in, home loan emi calculator, auto loan calculator, personal loan calculator, refinance calculator, student loan calculator, loan affordability calculator, prepayment calculator, monthly payment calculator, interest rate calculator, amortization calculator, free financial calculator, AI financial calculator, mortgage calculator USA, home loan calculator UK, loan calculator UAE, mortgage calculator Canada, home loan calculator Australia, EMI calculator India, best mortgage calculator, online loan calculator, calculate monthly mortgage payment, how much can I borrow, loan repayment calculator, debt calculator, finance calculator, compound interest calculator',
    faq: [
      {
        question: "What financial calculators does FinCalc offer?",
        answer: "FinCalc offers 30+ free financial calculators including home loan EMI calculator, mortgage calculator, auto loan calculator, personal loan calculator, SIP mutual fund calculator, GST calculator, income tax calculator 2026, inflation calculator, compound interest calculator, loan comparison tool, refinance calculator, and more."
      },
      {
        question: "Is FinCalc free to use?",
        answer: "Yes, all basic financial calculators on FinCalc are completely free to use with no account required. Premium features like saving calculations, PDF export, and AI financial advisor are available on paid plans."
      },
      {
        question: "Which countries does FinCalc support?",
        answer: "FinCalc supports financial calculations for users in USA, UK, UAE, Canada, Australia, and India. We have region-specific calculators including India EMI and GST calculators, US mortgage calculators, UK mortgage calculators with stamp duty, and UAE home finance tools."
      },
      {
        question: "How accurate are FinCalc's calculations?",
        answer: "FinCalc uses industry-standard financial formulas including the standard amortization formula for EMI calculations: [P × R × (1+R)^N] / [(1+R)^N - 1]. Our calculations are verified against official bank calculators from SBI, HDFC, Chase, Barclays, and other leading financial institutions."
      },
      {
        question: "How do I calculate my monthly mortgage payment?",
        answer: "Use FinCalc's free mortgage calculator. Enter your loan amount, interest rate, and loan term to instantly get your monthly payment, total interest, and full amortization schedule."
      },
      {
        question: "What is an EMI calculator?",
        answer: "An EMI (Equated Monthly Installment) calculator helps you calculate the fixed monthly payment for a loan. FinCalc offers free EMI calculators for home loans, auto loans, personal loans, and more."
      },
      {
        question: "How much home loan can I afford?",
        answer: "FinCalc's loan affordability calculator estimates the maximum loan amount based on your monthly income, expenses, interest rate, and desired loan tenure."
      },
      {
        question: "Can I calculate loan repayments in USD, GBP, AED or INR?",
        answer: "FinCalc works with any currency. Simply enter your loan amount in your local currency (USD, GBP, AED, CAD, AUD, INR, etc.) and get accurate repayment calculations instantly."
      }
    ],
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'FinCalc — Free AI Financial & Social Estimator (TikTok, SIP, EMI)',
      url: 'https://financecal.org/',
      description: 'Free AI-powered TikTok money calculator, SIP/SWP returns, mortgage estimator, and home loan EMI calculators with expert insights.',
      isPartOf: { '@type': 'WebSite', name: 'FinCalc', url: 'https://financecal.org' },
      mainEntity: {
        '@type': 'ItemList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'TikTok Money Calculator' },
          { '@type': 'ListItem', position: 2, name: 'SIP Investment Estimator' },
          { '@type': 'ListItem', position: 3, name: 'Mortgage & Home Loan EMI' },
          { '@type': 'ListItem', position: 4, name: 'Auto Loan & Trade-in Estimator' }
        ]
      }
    }
  });

  // Handle hash navigation from other pages (e.g. /blog clicking "Features")
  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    if (hash) {
      // Wait for page to render then scroll
      const timer = setTimeout(() => {
        const el = document.getElementById(hash);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 100);
      return () => clearTimeout(timer);
    }
  }, []);

  // Proactive preloading
  useEffect(() => {
    // Small delay to ensure Hero (LCP) is prioritized
    const timer = setTimeout(preloadHomeSections, 1500);
    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      {HelmetComponent}
      <div className="font-display antialiased">
        <div className="relative flex h-auto min-h-screen w-full flex-col overflow-x-hidden gap-0">
          <Hero />
          <React.Suspense fallback={<div style={{ minHeight: '920px' }} />}>
            <TrustedBy />
          </React.Suspense>

          <div className="h-px w-full bg-gradient-to-r from-transparent via-white/10 to-transparent" />
          <div id="features">
            <React.Suspense fallback={<div className="h-96" />}>
              <Features />
            </React.Suspense>
          </div>

          <div className="h-px w-full bg-gradient-to-r from-transparent via-white/10 to-transparent" />
          <div id="calculators">
            <React.Suspense fallback={<div className="h-[600px]" />}>
              <Calculators />
            </React.Suspense>
          </div>

          <div className="h-px w-full bg-gradient-to-r from-transparent via-white/10 to-transparent" />
          <div id="resources">
            <React.Suspense fallback={<div className="h-64" />}>
              <CTA />
            </React.Suspense>
          </div>

          <div className="h-px w-full bg-gradient-to-r from-transparent via-white/10 to-transparent" />
          <div>
            <React.Suspense fallback={<div className="h-48" />}>
              <NewsletterSignup />
            </React.Suspense>
          </div>
        </div>
      </div>
    </>
  );
};

export default Home;